import { forwardRef, useId, useState } from 'react';
import Button from './Button';

function useField({ id, helper, error, 'aria-describedby': describedBy }) {
  const generatedId = useId();
  const controlId = id || `fc-field-${generatedId}`;
  return {
    id: controlId,
    helperId: `${controlId}-helper`, errorId: `${controlId}-error`,
    describedBy: [describedBy, helper && `${controlId}-helper`, error && `${controlId}-error`]
      .filter(Boolean).join(' ') || undefined,
  };
}

function FieldFrame({ label, helper, error, required, field, children }) {
  return <div className="fc-field">
    <label className="fc-label" htmlFor={field.id}>{label}
      {required && <span className="fc-required"> (required)</span>}</label>
    {children}
    {helper && <p id={field.helperId} className="fc-helper">{helper}</p>}
    {error && <p id={field.errorId} className="fc-field-error">{error}</p>}
  </div>;
}

export const TextField = forwardRef(function TextField({
  label, helper, error, id, required, className = '',
  'aria-describedby': describedBy, ...props
}, ref) {
  const field = useField({ id, helper, error, 'aria-describedby': describedBy });
  return <FieldFrame {...{ label, helper, error, required, field }}>
    <input {...props} ref={ref} id={field.id} required={required}
      className={`fc-control ${className}`} aria-invalid={Boolean(error) || undefined}
      aria-describedby={field.describedBy} />
  </FieldFrame>;
});

export const PasswordField = forwardRef(function PasswordField({
  label, helper, error, id, required, className = '', disabled,
  'aria-describedby': describedBy, ...props
}, ref) {
  const [visible, setVisible] = useState(false);
  const field = useField({ id, helper, error, 'aria-describedby': describedBy });
  return <FieldFrame {...{ label, helper, error, required, field }}>
    <div className="fc-password">
      <input {...props} ref={ref} id={field.id} type={visible ? 'text' : 'password'}
        required={required} disabled={disabled} className={`fc-control ${className}`}
        aria-invalid={Boolean(error) || undefined} aria-describedby={field.describedBy} />
      <Button variant="secondary" type="button" className="fc-password__toggle" disabled={disabled}
        aria-label={`${visible ? 'Hide' : 'Show'} ${typeof label === 'string' ? label.toLowerCase() : 'password'}`}
        aria-controls={field.id} aria-pressed={visible} onClick={() => setVisible(!visible)}>
        {visible ? 'Hide' : 'Show'}
      </Button>
    </div>
  </FieldFrame>;
});

export const SelectField = forwardRef(function SelectField({
  label, helper, error, id, required, children, className = '',
  'aria-describedby': describedBy, ...props
}, ref) {
  const field = useField({ id, helper, error, 'aria-describedby': describedBy });
  return <FieldFrame {...{ label, helper, error, required, field }}>
    <select {...props} ref={ref} id={field.id} required={required}
      className={`fc-control ${className}`} aria-invalid={Boolean(error) || undefined}
      aria-describedby={field.describedBy}>{children}</select>
  </FieldFrame>;
});
